/**
 * Client Detail Page (Admin View) - Epic 2 & 4
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  FileText,
  Loader2,
  Save,
  Power,
  Apple,
  MessageSquare,
  CheckSquare,
  Crown,
  Edit2,
  X,
  Dumbbell,
  Plus,
  CalendarDays,
  Ruler,
  Camera,
  ImageIcon,
  Trash2,
  Target,
  MessageCircle,
} from "lucide-react";
import { useClientDetail, updateFeatureToggles, toggleClientActive } from "../hooks/useClient";
import { useWorkoutTemplates } from "../hooks/useWorkout";
import { useClientWorkouts, assignWorkout } from "../hooks/useSession";
import { useClientMeasurements, useClientPhotos } from "../hooks/useMeasurements";
import { useClientNutrition, useClientHabits, useClientCheckins } from "../hooks/useNutrition";
import { useClientNutritionTargets, setNutritionTargets, adminCreateHabit, adminDeleteHabit } from "../hooks/useMessaging";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Toaster, toast } from "sonner";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { client, isLoading, isError, mutate } = useClientDetail(clientId);
  const { templates } = useWorkoutTemplates();
  const { workouts: assignedWorkouts, mutate: mutateWorkouts } = useClientWorkouts(clientId);
  const { measurements } = useClientMeasurements(clientId);
  const { photos } = useClientPhotos(clientId);
  const { logs: nutritionLogs } = useClientNutrition(clientId);
  const { habits: clientHabits, mutate: mutateHabits } = useClientHabits(clientId);
  const { checkins } = useClientCheckins(clientId);
  const { targets: nutritionTargets, mutate: mutateTargets } = useClientNutritionTargets(clientId);
  
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    notes: "",
    gender: null,
  });
  
  const [features, setFeatures] = useState({
    has_nutrition: false,
    has_messaging: false,
    has_habits: false,
  });

  // MVP+ state
  const [newHabitName, setNewHabitName] = useState("");
  const [newHabitDesc, setNewHabitDesc] = useState("");
  const [targetForm, setTargetForm] = useState({
    target_calories: "", target_protein: "", target_carbs: "", target_fats: "",
  });
  const [habitDialogOpen, setHabitDialogOpen] = useState(false);
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  
  useEffect(() => {
    if (client) {
      setProfileData({
        first_name: client.first_name || "",
        last_name: client.last_name || "",
        phone: client.phone || "",
        notes: client.notes || "",
        gender: client.gender || null,
      });
      setFeatures({
        has_nutrition: client.has_nutrition,
        has_messaging: client.has_messaging,
        has_habits: client.has_habits,
      });
    }
  }, [client]);

  useEffect(() => {
    if (nutritionTargets) {
      setTargetForm({
        target_calories: nutritionTargets.target_calories ?? "",
        target_protein: nutritionTargets.target_protein ?? "",
        target_carbs: nutritionTargets.target_carbs ?? "",
        target_fats: nutritionTargets.target_fats ?? "",
      });
    }
  }, [nutritionTargets]);
  
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.post(`/api/admin/clients/${clientId}/profile`, profileData);
      toast.success("Profil mis à jour");
      setEditMode(false);
      mutate();
    } catch (err) {
      toast.error(err.message || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };
  
  const handleFeatureToggle = async (feature, value) => {
    const newFeatures = { ...features, [feature]: value };
    setFeatures(newFeatures);
    
    try {
      await updateFeatureToggles(clientId, { [feature]: value });
      toast.success(`Module ${feature === 'has_nutrition' ? 'Nutrition' : feature === 'has_messaging' ? 'Messagerie' : 'Habitudes'} ${value ? 'activé' : 'désactivé'}`);
      mutate();
    } catch (err) {
      // Revert on error
      setFeatures({ ...features, [feature]: !value });
      toast.error(err.message || "Erreur lors de la mise à jour");
    }
  };
  
  const handleToggleActive = async () => {
    const newStatus = !client.is_active;
    try {
      await toggleClientActive(clientId, newStatus);
      toast.success(newStatus ? "Client réactivé" : "Client désactivé");
      mutate();
    } catch (err) {
      toast.error(err.message || "Erreur lors de la mise à jour");
    }
  };
  
  const handleAssignWorkout = async () => {
    if (!selectedTemplate || !selectedDate) {
      toast.error("Sélectionnez un programme et une date");
      return;
    }
    
    setAssigning(true);
    try {
      await assignWorkout(clientId, selectedTemplate, selectedDate);
      toast.success("Séance assignée avec succès");
      setAssignDialogOpen(false);
      setSelectedTemplate("");
      setSelectedDate("");
      mutateWorkouts();
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'assignation");
    } finally {
      setAssigning(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-lime-400" />
      </div>
    );
  }
  
  if (isError || !client) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Card className="bg-zinc-900/50 border-zinc-800 max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-zinc-400 mb-4">Client non trouvé</p>
            <Button onClick={() => navigate("/admin")} variant="outline">
              Retour au dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-950">
      <Toaster position="top-right" richColors />
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin")}
              data-testid="back-button"
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </Button>
            
            <div className="flex items-center gap-2">
              <Badge 
                variant={client.is_active ? "default" : "secondary"}
                className={client.is_active 
                  ? "bg-emerald-400/10 text-emerald-400 border-emerald-400/30" 
                  : "bg-zinc-800 text-zinc-500"}
              >
                {client.is_active ? "Actif" : "Inactif"}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleActive}
                data-testid="toggle-active-button"
                className={client.is_active 
                  ? "border-red-500/50 text-red-400 hover:bg-red-500/10"
                  : "border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"}
              >
                <Power className="w-4 h-4 mr-1" />
                {client.is_active ? "Désactiver" : "Réactiver"}
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="space-y-8"
        >
          {/* Client Header */}
          <motion.section variants={itemVariants}>
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 bg-zinc-800 rounded-2xl flex items-center justify-center">
                <span className="text-lime-400 font-bold text-3xl font-heading">
                  {(client.first_name?.[0] || client.email[0]).toUpperCase()}
                </span>
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-white tracking-tight">
                  {client.first_name && client.last_name 
                    ? `${client.first_name} ${client.last_name}`
                    : client.email}
                </h1>
                <p className="text-zinc-500 mt-1">{client.email}</p>
                {client.current_subscription && (
                  <Badge className="mt-2 bg-lime-400/10 text-lime-400 border-lime-400/30">
                    <Crown className="w-3 h-3 mr-1" />
                    {client.current_subscription.plan_name}
                  </Badge>
                )}
              </div>
            </div>
          </motion.section>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Profile Card */}
            <motion.section variants={itemVariants}>
              <Card className="bg-zinc-900/50 border-zinc-800 h-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl text-white">
                      Informations
                    </CardTitle>
                    {!editMode ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditMode(true)}
                        data-testid="edit-profile-button"
                        className="text-zinc-400 hover:text-lime-400"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditMode(false)}
                          className="text-zinc-400"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveProfile}
                          disabled={saving}
                          data-testid="save-profile-button"
                          className="bg-lime-400 hover:bg-lime-500 text-zinc-950"
                        >
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                          Sauver
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {editMode ? (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-zinc-400">Prénom</Label>
                          <Input
                            value={profileData.first_name}
                            onChange={(e) => setProfileData({...profileData, first_name: e.target.value})}
                            data-testid="edit-firstname"
                            className="bg-zinc-950 border-zinc-800"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-zinc-400">Nom</Label>
                          <Input
                            value={profileData.last_name}
                            onChange={(e) => setProfileData({...profileData, last_name: e.target.value})}
                            data-testid="edit-lastname"
                            className="bg-zinc-950 border-zinc-800"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Téléphone</Label>
                        <Input
                          value={profileData.phone}
                          onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                          data-testid="edit-phone"
                          className="bg-zinc-950 border-zinc-800"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Genre</Label>
                        <Select
                          value={profileData.gender || ""}
                          onValueChange={(value) => setProfileData({...profileData, gender: value || null})}
                        >
                          <SelectTrigger className="bg-zinc-950 border-zinc-800">
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            <SelectItem value="male">Homme</SelectItem>
                            <SelectItem value="female">Femme</SelectItem>
                            <SelectItem value="other">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-400">Notes (privées)</Label>
                        <Textarea
                          value={profileData.notes}
                          onChange={(e) => setProfileData({...profileData, notes: e.target.value})}
                          placeholder="Notes personnelles sur le client..."
                          data-testid="edit-notes"
                          className="bg-zinc-950 border-zinc-800 min-h-[100px]"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 text-zinc-300">
                        <User className="w-5 h-5 text-zinc-500" />
                        <span>{client.first_name || "-"} {client.last_name || ""}</span>
                      </div>
                      <div className="flex items-center gap-3 text-zinc-300">
                        <Mail className="w-5 h-5 text-zinc-500" />
                        <span>{client.email}</span>
                      </div>
                      <div className="flex items-center gap-3 text-zinc-300">
                        <Phone className="w-5 h-5 text-zinc-500" />
                        <span>{client.phone || "-"}</span>
                      </div>
                      <div className="flex items-center gap-3 text-zinc-300">
                        <Calendar className="w-5 h-5 text-zinc-500" />
                        <span>Inscrit le {new Date(client.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                      {client.notes && (
                        <>
                          <Separator className="bg-zinc-800" />
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-zinc-500 text-sm">
                              <FileText className="w-4 h-4" />
                              Notes
                            </div>
                            <p className="text-zinc-400 text-sm whitespace-pre-wrap">
                              {client.notes}
                            </p>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.section>
            
            {/* Feature Toggles Card */}
            <motion.section variants={itemVariants}>
              <Card className="bg-zinc-900/50 border-zinc-800 h-full">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-white">
                    Modules Activés
                  </CardTitle>
                  <CardDescription className="text-zinc-500">
                    Gérez les accès aux fonctionnalités premium
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Nutrition Toggle */}
                  <div className="flex items-center justify-between p-4 bg-zinc-950/50 rounded-xl border border-zinc-800">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        features.has_nutrition ? 'bg-emerald-400/10' : 'bg-zinc-800'
                      }`}>
                        <Apple className={`w-6 h-6 ${
                          features.has_nutrition ? 'text-emerald-400' : 'text-zinc-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-semibold text-white">Nutrition</p>
                        <p className="text-sm text-zinc-500">Suivi calories & macros</p>
                      </div>
                    </div>
                    <Switch
                      checked={features.has_nutrition}
                      onCheckedChange={(checked) => handleFeatureToggle('has_nutrition', checked)}
                      data-testid="toggle-nutrition-feature"
                      className="data-[state=checked]:bg-lime-400"
                    />
                  </div>
                  
                  {/* Messaging Toggle */}
                  <div className="flex items-center justify-between p-4 bg-zinc-950/50 rounded-xl border border-zinc-800">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        features.has_messaging ? 'bg-purple-400/10' : 'bg-zinc-800'
                      }`}>
                        <MessageSquare className={`w-6 h-6 ${
                          features.has_messaging ? 'text-purple-400' : 'text-zinc-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-semibold text-white">Messagerie</p>
                        <p className="text-sm text-zinc-500">Chat avec le coach</p>
                      </div>
                    </div>
                    <Switch
                      checked={features.has_messaging}
                      onCheckedChange={(checked) => handleFeatureToggle('has_messaging', checked)}
                      data-testid="toggle-messaging-feature"
                      className="data-[state=checked]:bg-lime-400"
                    />
                  </div>
                  
                  {/* Habits Toggle */}
                  <div className="flex items-center justify-between p-4 bg-zinc-950/50 rounded-xl border border-zinc-800">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        features.has_habits ? 'bg-amber-400/10' : 'bg-zinc-800'
                      }`}>
                        <CheckSquare className={`w-6 h-6 ${
                          features.has_habits ? 'text-amber-400' : 'text-zinc-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-semibold text-white">Habitudes</p>
                        <p className="text-sm text-zinc-500">Habit tracker quotidien</p>
                      </div>
                    </div>
                    <Switch
                      checked={features.has_habits}
                      onCheckedChange={(checked) => handleFeatureToggle('has_habits', checked)}
                      data-testid="toggle-habits-feature"
                      className="data-[state=checked]:bg-lime-400"
                    />
                  </div>
                </CardContent>
              </Card>
            </motion.section>
          </div>
          
          {/* Subscription History */}
          <motion.section variants={itemVariants}>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-white">
                  Historique des Abonnements
                </CardTitle>
              </CardHeader>
              <CardContent>
                {client.subscriptions && client.subscriptions.length > 0 ? (
                  <div className="space-y-3">
                    {client.subscriptions.map((sub) => (
                      <div 
                        key={sub.id}
                        className="flex items-center justify-between p-4 bg-zinc-950/50 rounded-xl border border-zinc-800"
                      >
                        <div>
                          <p className="font-semibold text-white">{sub.plan_name}</p>
                          <p className="text-sm text-zinc-500">
                            Depuis le {new Date(sub.start_date).toLocaleDateString('fr-FR')}
                            {sub.end_date && ` jusqu'au ${new Date(sub.end_date).toLocaleDateString('fr-FR')}`}
                          </p>
                        </div>
                        <Badge 
                          className={
                            sub.status === 'active' 
                              ? "bg-emerald-400/10 text-emerald-400 border-emerald-400/30"
                              : sub.status === 'expired'
                              ? "bg-zinc-800 text-zinc-500"
                              : "bg-red-400/10 text-red-400 border-red-400/30"
                          }
                        >
                          {sub.status === 'active' ? 'Actif' : sub.status === 'expired' ? 'Expiré' : 'Annulé'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-center py-4">Aucun abonnement</p>
                )}
              </CardContent>
            </Card>
          </motion.section>
          
          {/* Assigned Workouts - Epic 4 */}
          <motion.section variants={itemVariants}>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl text-white">
                    Séances Assignées
                  </CardTitle>
                  <Button
                    onClick={() => setAssignDialogOpen(true)}
                    data-testid="assign-workout-button"
                    className="h-9 px-4 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Assigner
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {assignedWorkouts && assignedWorkouts.length > 0 ? (
                  <div className="space-y-3">
                    {assignedWorkouts.map((workout) => (
                      <div 
                        key={workout.id}
                        className="flex items-center justify-between p-4 bg-zinc-950/50 rounded-xl border border-zinc-800"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-lime-400/10 rounded-lg flex items-center justify-center">
                            <Dumbbell className="w-5 h-5 text-lime-400" />
                          </div>
                          <div>
                            <p className="font-semibold text-white">{workout.template_name}</p>
                            <p className="text-sm text-zinc-500">
                              {new Date(workout.scheduled_date).toLocaleDateString('fr-FR', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long'
                              })}
                            </p>
                          </div>
                        </div>
                        <Badge 
                          className={
                            workout.status === 'completed' 
                              ? "bg-emerald-400/10 text-emerald-400 border-emerald-400/30"
                              : workout.status === 'in_progress'
                              ? "bg-amber-400/10 text-amber-400 border-amber-400/30"
                              : "bg-zinc-800 text-zinc-400"
                          }
                        >
                          {workout.status === 'completed' ? 'Terminée' 
                            : workout.status === 'in_progress' ? 'En cours' 
                            : 'Planifiée'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-center py-4">Aucune séance assignée</p>
                )}
              </CardContent>
            </Card>
          </motion.section>

          {/* Body Measurements - Epic 4 */}
          <motion.section variants={itemVariants}>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-white flex items-center gap-2">
                  <Ruler className="w-5 h-5 text-sky-400" />
                  Mensurations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {measurements && measurements.length > 0 ? (
                  <div className="space-y-3">
                    {measurements.slice(0, 5).map((m) => (
                      <div key={m.id} className="p-4 bg-zinc-950/50 rounded-xl border border-zinc-800" data-testid={`admin-measurement-${m.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold text-white text-sm">
                            {new Date(m.measured_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          {m.weight_kg != null && (
                            <div><p className="text-lg font-bold text-white">{m.weight_kg}</p><p className="text-xs text-zinc-500">kg</p></div>
                          )}
                          {m.body_fat_percent != null && (
                            <div><p className="text-lg font-bold text-white">{m.body_fat_percent}</p><p className="text-xs text-zinc-500">% BF</p></div>
                          )}
                          {m.chest_cm != null && (
                            <div><p className="text-lg font-bold text-white">{m.chest_cm}</p><p className="text-xs text-zinc-500">Poit.</p></div>
                          )}
                          {m.waist_cm != null && (
                            <div><p className="text-lg font-bold text-white">{m.waist_cm}</p><p className="text-xs text-zinc-500">Taille</p></div>
                          )}
                          {m.hips_cm != null && (
                            <div><p className="text-lg font-bold text-white">{m.hips_cm}</p><p className="text-xs text-zinc-500">Hanch.</p></div>
                          )}
                          {m.arm_cm != null && (
                            <div><p className="text-lg font-bold text-white">{m.arm_cm}</p><p className="text-xs text-zinc-500">Bras</p></div>
                          )}
                          {m.thigh_cm != null && (
                            <div><p className="text-lg font-bold text-white">{m.thigh_cm}</p><p className="text-xs text-zinc-500">Cuiss.</p></div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-center py-4">Aucune mesure enregistrée</p>
                )}
              </CardContent>
            </Card>
          </motion.section>

          {/* Progress Photos - Epic 4 */}
          <motion.section variants={itemVariants}>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-white flex items-center gap-2">
                  <Camera className="w-5 h-5 text-purple-400" />
                  Photos d'évolution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {photos && photos.length > 0 ? (
                  <div className="grid grid-cols-3 gap-3">
                    {photos.map((photo) => (
                      <div key={photo.id} className="aspect-[3/4] rounded-lg overflow-hidden relative group" data-testid={`admin-photo-${photo.id}`}>
                        <img
                          src={photo.photo_url}
                          alt={`${photo.photo_type} - ${photo.taken_at}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                          <p className="text-xs text-white capitalize">{photo.photo_type}</p>
                          <p className="text-xs text-zinc-400">{new Date(photo.taken_at).toLocaleDateString('fr-FR')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <ImageIcon className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                    <p className="text-zinc-500">Aucune photo d'évolution</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.section>

          {/* Nutrition Logs + Targets - MVP+ */}
          <motion.section variants={itemVariants}>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl text-white flex items-center gap-2">
                    <Apple className="w-5 h-5 text-orange-400" />
                    Nutrition
                  </CardTitle>
                  <Button size="sm" onClick={() => setTargetDialogOpen(true)} data-testid="set-targets-button"
                    className="bg-orange-400/10 hover:bg-orange-400/20 text-orange-400 border border-orange-400/30 rounded-full h-8 px-3 text-xs font-bold">
                    <Target className="w-3 h-3 mr-1" /> Objectifs
                  </Button>
                </div>
                {nutritionTargets && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {nutritionTargets.target_calories != null && <Badge className="bg-orange-400/10 text-orange-400 text-xs">{nutritionTargets.target_calories} kcal</Badge>}
                    {nutritionTargets.target_protein != null && <Badge className="bg-red-400/10 text-red-400 text-xs">{nutritionTargets.target_protein}g P</Badge>}
                    {nutritionTargets.target_carbs != null && <Badge className="bg-amber-400/10 text-amber-400 text-xs">{nutritionTargets.target_carbs}g C</Badge>}
                    {nutritionTargets.target_fats != null && <Badge className="bg-sky-400/10 text-sky-400 text-xs">{nutritionTargets.target_fats}g F</Badge>}
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {nutritionLogs && nutritionLogs.length > 0 ? (
                  <div className="space-y-2">
                    {nutritionLogs.slice(0, 7).map((log) => (
                      <div key={log.id} className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-lg border border-zinc-800" data-testid={`admin-nutrition-${log.id}`}>
                        <p className="text-sm font-semibold text-white">
                          {new Date(log.log_date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}
                        </p>
                        <div className="flex gap-3 text-xs">
                          <Badge className="bg-orange-400/10 text-orange-400 border-orange-400/30">{log.calories_kcal} kcal</Badge>
                          <span className="text-red-400">{log.protein_g}P</span>
                          <span className="text-amber-400">{log.carbs_g}C</span>
                          <span className="text-sky-400">{log.fat_g}F</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-center py-4">Aucun log nutrition</p>
                )}
              </CardContent>
            </Card>
          </motion.section>

          {/* Habits - Coach-Managed (MVP+) */}
          <motion.section variants={itemVariants}>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-xl text-white flex items-center gap-2">
                    <CheckSquare className="w-5 h-5 text-emerald-400" />
                    Habitudes
                  </CardTitle>
                  <Button size="sm" onClick={() => setHabitDialogOpen(true)} data-testid="add-habit-for-client"
                    className="bg-emerald-400 hover:bg-emerald-500 text-zinc-950 font-bold text-xs rounded-full h-8 px-3">
                    <Plus className="w-3 h-3 mr-1" /> Ajouter
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {clientHabits && clientHabits.length > 0 ? (
                  <div className="space-y-2">
                    {clientHabits.map((h) => (
                      <div key={h.id} className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-lg border border-zinc-800" data-testid={`admin-habit-${h.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full border-2 border-emerald-400 bg-emerald-400/10 flex items-center justify-center">
                            <CheckSquare className="w-3 h-3 text-emerald-400" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-white">{h.name}</p>
                            {h.description && <p className="text-xs text-zinc-500">{h.description}</p>}
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" data-testid={`delete-habit-${h.id}`}
                          onClick={async () => {
                            try {
                              await adminDeleteHabit(clientId, h.id);
                              toast.success("Habitude supprimée");
                              mutateHabits();
                            } catch (e) { toast.error(e.message); }
                          }}
                          className="text-zinc-500 hover:text-red-400 flex-shrink-0">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-center py-4">Aucune habitude définie</p>
                )}
              </CardContent>
            </Card>
          </motion.section>

          {/* Weekly Check-ins - Epic 5 */}
          <motion.section variants={itemVariants}>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-violet-400" />
                  Bilans Hebdomadaires
                </CardTitle>
              </CardHeader>
              <CardContent>
                {checkins && checkins.length > 0 ? (
                  <div className="space-y-3">
                    {checkins.slice(0, 5).map((c) => (
                      <div key={c.id} className="p-4 bg-zinc-950/50 rounded-xl border border-zinc-800" data-testid={`admin-checkin-${c.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <Badge className="bg-violet-400/10 text-violet-400 border-violet-400/30 text-xs">
                            {new Date(c.checkin_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-center">
                          <div className="p-2 bg-zinc-900/50 rounded-lg">
                            <p className="text-lg font-bold text-lime-400">{c.energy_level}</p>
                            <p className="text-xs text-zinc-500">Énergie</p>
                          </div>
                          <div className="p-2 bg-zinc-900/50 rounded-lg">
                            <p className="text-lg font-bold text-violet-400">{c.sleep_quality}</p>
                            <p className="text-xs text-zinc-500">Sommeil</p>
                          </div>
                          <div className="p-2 bg-zinc-900/50 rounded-lg">
                            <p className="text-lg font-bold text-rose-400">{c.stress_level}</p>
                            <p className="text-xs text-zinc-500">Stress</p>
                          </div>
                        </div>
                        {c.remarks && <p className="text-sm text-zinc-400 mt-3 italic">"{c.remarks}"</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-zinc-500 text-center py-4">Aucun bilan soumis</p>
                )}
              </CardContent>
            </Card>
          </motion.section>
        </motion.div>
      </main>
      
      {/* Assign Workout Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">
              Assigner une séance
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Sélectionnez un programme et une date
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Programme</Label>
              <Select
                value={selectedTemplate}
                onValueChange={setSelectedTemplate}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-800 h-11" data-testid="select-template-assign">
                  <SelectValue placeholder="Sélectionner un programme" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Date</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                data-testid="select-date-assign"
                className="bg-zinc-950 border-zinc-800 h-11"
              />
            </div>
            
            <Button
              onClick={handleAssignWorkout}
              disabled={assigning || !selectedTemplate || !selectedDate}
              data-testid="confirm-assign-button"
              className="w-full h-12 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full mt-4"
            >
              {assigning ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Assigner
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Habit Dialog (Coach) */}
      <Dialog open={habitDialogOpen} onOpenChange={setHabitDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">Créer une habitude</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-zinc-300">Nom</Label>
              <Input value={newHabitName} onChange={(e) => setNewHabitName(e.target.value)} placeholder="Ex: Boire 2L d'eau" data-testid="coach-habit-name" className="bg-zinc-950 border-zinc-800 h-11" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Description (optionnel)</Label>
              <Input value={newHabitDesc} onChange={(e) => setNewHabitDesc(e.target.value)} placeholder="Détails..." data-testid="coach-habit-desc" className="bg-zinc-950 border-zinc-800 h-11" />
            </div>
            <Button
              disabled={!newHabitName.trim()}
              data-testid="submit-coach-habit"
              onClick={async () => {
                try {
                  await adminCreateHabit(clientId, { name: newHabitName, description: newHabitDesc || null });
                  toast.success("Habitude créée");
                  setNewHabitName(""); setNewHabitDesc("");
                  setHabitDialogOpen(false);
                  mutateHabits();
                } catch (e) { toast.error(e.message); }
              }}
              className="w-full h-12 bg-emerald-400 hover:bg-emerald-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
            >Créer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set Nutrition Targets Dialog (Coach) */}
      <Dialog open={targetDialogOpen} onOpenChange={setTargetDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white">Objectifs Nutritionnels</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Calories (kcal)</Label>
                <Input type="number" value={targetForm.target_calories} onChange={(e) => setTargetForm({ ...targetForm, target_calories: e.target.value })} placeholder="2200" data-testid="target-calories" className="bg-zinc-950 border-zinc-800 h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Protéines (g)</Label>
                <Input type="number" step="0.1" value={targetForm.target_protein} onChange={(e) => setTargetForm({ ...targetForm, target_protein: e.target.value })} placeholder="180" data-testid="target-protein" className="bg-zinc-950 border-zinc-800 h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Glucides (g)</Label>
                <Input type="number" step="0.1" value={targetForm.target_carbs} onChange={(e) => setTargetForm({ ...targetForm, target_carbs: e.target.value })} placeholder="250" data-testid="target-carbs" className="bg-zinc-950 border-zinc-800 h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Lipides (g)</Label>
                <Input type="number" step="0.1" value={targetForm.target_fats} onChange={(e) => setTargetForm({ ...targetForm, target_fats: e.target.value })} placeholder="70" data-testid="target-fats" className="bg-zinc-950 border-zinc-800 h-10" />
              </div>
            </div>
            <Button
              data-testid="submit-targets"
              onClick={async () => {
                try {
                  await setNutritionTargets(clientId, {
                    target_calories: targetForm.target_calories ? parseInt(targetForm.target_calories) : null,
                    target_protein: targetForm.target_protein ? parseFloat(targetForm.target_protein) : null,
                    target_carbs: targetForm.target_carbs ? parseFloat(targetForm.target_carbs) : null,
                    target_fats: targetForm.target_fats ? parseFloat(targetForm.target_fats) : null,
                  });
                  toast.success("Objectifs mis à jour");
                  setTargetDialogOpen(false);
                  mutateTargets();
                } catch (e) { toast.error(e.message); }
              }}
              className="w-full h-12 bg-orange-400 hover:bg-orange-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
            >Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
